import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Server,
  Globe,
  Shield,
  Cpu,
  Trash2,
  Edit,
  Copy,
  Check,
  ExternalLink,
  Terminal,
  Download,
  Loader2,
  HardDrive,
  MemoryStick,
  Network,
  Users,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { api } from '@/api/client';
import { clsx } from 'clsx';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import type { VpsEntity, SshAttempt, SystemMetrics, NetworkMetrics, VpsSecurityDetail, ThreatLevel, VpsHealthSummary, EndpointHealth } from '@pulse/shared';
import { VPS_PROVIDER_LABELS, VPS_STATUS_CONFIG } from '@pulse/shared';

type Tab = 'overview' | 'metrics' | 'security' | 'external';
type TimeRange = '1h' | '6h' | '24h' | '7d';

const TIME_RANGE_CONFIG: Record<TimeRange, { label: string; ms: number; resolution: string }> = {
  '1h':  { label: '1h',  ms: 1 * 60 * 60 * 1000,      resolution: '1m' },
  '6h':  { label: '6h',  ms: 6 * 60 * 60 * 1000,      resolution: '5m' },
  '24h': { label: '24h', ms: 24 * 60 * 60 * 1000,     resolution: '5m' },
  '7d':  { label: '7d',  ms: 7 * 24 * 60 * 60 * 1000, resolution: '1h' },
};

const PULSE_URL = import.meta.env.VITE_PULSE_URL || 'https://pulse.setype.com';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatBytesRate(bytesPerSec: number): string {
  return `${formatBytes(bytesPerSec)}/s`;
}

function countryCodeToFlag(code: string | null): string {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  );
}

function formatRelativeTime(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function getTimestamp(m: any): string {
  return m.time || m.timestamp;
}

const chartTooltipStyle = {
  contentStyle: { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' },
  labelStyle: { color: '#9ca3af' },
};

function AgentSetup({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const installCmd = `curl -fsSL ${PULSE_URL}/api/agent/install.sh | sudo bash -s ${token}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(installCmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-6 rounded-lg border border-pulse-500/30 bg-gray-900/80 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-lg bg-pulse-500/10">
          <Terminal className="h-5 w-5 text-pulse-400" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-100">Install Agent</h2>
          <p className="text-sm text-gray-400">Run this command on your server to install the monitoring agent</p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <code className="flex-1 px-4 py-3 bg-black/50 rounded-lg text-sm font-mono text-green-400 overflow-x-auto whitespace-nowrap">
          {installCmd}
        </code>
        <button
          onClick={handleCopy}
          className="btn-ghost p-3 shrink-0"
          title="Copy command"
        >
          {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-pulse-400" />
        Waiting for agent to connect...
      </div>
    </div>
  );
}

export function VpsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [copiedToken, setCopiedToken] = useState(false);
  const [showReinstall, setShowReinstall] = useState(false);

  const { data: vps, isLoading, error } = useQuery({
    queryKey: ['vps', id],
    queryFn: () => api.get<VpsEntity>(`/vps/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 5s when agent has never connected or during reinstall
      if (!data?.lastSeenAt || showReinstall) return 5000;
      return false;
    },
  });

  // Auto-hide reinstall banner once agent reconnects
  useEffect(() => {
    if (showReinstall && vps?.lastSeenAt) {
      setShowReinstall(false);
    }
  }, [showReinstall, vps?.lastSeenAt]);

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/vps/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vps'] });
      navigate('/');
    },
  });

  const regenerateTokenMutation = useMutation({
    mutationFn: () => api.post<{ agentToken: string }>(`/vps/${id}/regenerate-token`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vps', id] });
      setShowReinstall(true);
    },
  });

  const handleCopyToken = async () => {
    if (vps?.agentToken) {
      await navigator.clipboard.writeText(vps.agentToken);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${vps?.name}"? This cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  const handleReinstall = () => {
    if (window.confirm('This will revoke the current token. The agent on this server will stop working until reinstalled. Continue?')) {
      regenerateTokenMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pulse-400" />
      </div>
    );
  }

  if (error || !vps) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">VPS not found</p>
        <Link to="/" className="text-pulse-400 hover:text-pulse-300 mt-2 inline-block">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const statusConfig = VPS_STATUS_CONFIG[vps.status];
  const showSetupBanner = !vps.lastSeenAt || showReinstall;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="p-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-gray-100">{vps.name}</h1>
              <span
                className={clsx(
                  'status-badge',
                  `status-${vps.status}`
                )}
              >
                {statusConfig.label}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {vps.ipAddress} · {VPS_PROVIDER_LABELS[vps.provider] || vps.provider}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/vps/${id}/edit`}
            className="btn-secondary"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="btn-danger"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Agent Setup Banner */}
      {showSetupBanner && <AgentSetup token={vps.agentToken} />}

      {/* Tabs */}
      <div className="border-b border-gray-800 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={clsx(
              'flex items-center gap-2 py-3 border-b-2 transition-colors',
              activeTab === 'overview'
                ? 'border-pulse-400 text-pulse-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            )}
          >
            <Server className="h-4 w-4" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={clsx(
              'flex items-center gap-2 py-3 border-b-2 transition-colors',
              activeTab === 'metrics'
                ? 'border-pulse-400 text-pulse-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            )}
          >
            <Cpu className="h-4 w-4" />
            Metrics
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={clsx(
              'flex items-center gap-2 py-3 border-b-2 transition-colors',
              activeTab === 'security'
                ? 'border-pulse-400 text-pulse-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            )}
          >
            <Shield className="h-4 w-4" />
            Security
          </button>
          <button
            onClick={() => setActiveTab('external')}
            className={clsx(
              'flex items-center gap-2 py-3 border-b-2 transition-colors',
              activeTab === 'external'
                ? 'border-pulse-400 text-pulse-400'
                : 'border-transparent text-gray-400 hover:text-gray-200'
            )}
          >
            <Globe className="h-4 w-4" />
            API Health
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          vps={vps}
          onCopyToken={handleCopyToken}
          copiedToken={copiedToken}
          onReinstall={handleReinstall}
          isReinstalling={regenerateTokenMutation.isPending}
        />
      )}
      {activeTab === 'metrics' && <MetricsTab vpsId={vps.id} />}
      {activeTab === 'security' && <SecurityTab vpsId={vps.id} />}
      {activeTab === 'external' && <ExternalTab vpsId={vps.id} />}
    </div>
  );
}

function OverviewTab({
  vps,
  onCopyToken,
  copiedToken,
  onReinstall,
  isReinstalling,
}: {
  vps: VpsEntity;
  onCopyToken: () => void;
  copiedToken: boolean;
  onReinstall: () => void;
  isReinstalling: boolean;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Identity Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-sm font-medium text-gray-300">Identity</h2>
        </div>
        <div className="card-body space-y-4">
          <InfoRow label="Name" value={vps.name} />
          <InfoRow label="Hostname" value={vps.hostname || '-'} />
          <InfoRow label="Provider" value={VPS_PROVIDER_LABELS[vps.provider] || vps.provider} />
          <InfoRow label="IP Address" value={vps.ipAddress} mono />
          {vps.ipv6Address && <InfoRow label="IPv6" value={vps.ipv6Address} mono />}
          {vps.internalIp && <InfoRow label="Internal IP" value={vps.internalIp} mono />}
        </div>
      </div>

      {/* Connection Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-sm font-medium text-gray-300">Connection</h2>
        </div>
        <div className="card-body space-y-4">
          <InfoRow label="SSH Port" value={String(vps.sshPort)} />
          <InfoRow label="SSH User" value={vps.sshUser} />
          <div>
            <p className="text-sm text-gray-500 mb-1">Agent Token</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-800 rounded text-sm font-mono text-gray-400 truncate">
                {vps.agentToken.substring(0, 16)}...
              </code>
              <button
                onClick={onCopyToken}
                className="btn-ghost p-2"
                title="Copy token"
              >
                {copiedToken ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <button
            onClick={onReinstall}
            disabled={isReinstalling}
            className="btn-secondary w-full justify-center"
          >
            <Download className={clsx('h-4 w-4 mr-2', isReinstalling && 'animate-spin')} />
            Reinstall Agent
          </button>
        </div>
      </div>

      {/* Specs Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-sm font-medium text-gray-300">Specifications</h2>
        </div>
        <div className="card-body space-y-4">
          <InfoRow label="CPU Cores" value={vps.cpuCores ? String(vps.cpuCores) : '-'} />
          <InfoRow label="RAM" value={vps.ramMb ? `${(vps.ramMb / 1024).toFixed(1)} GB` : '-'} />
          <InfoRow label="Disk" value={vps.diskGb ? `${vps.diskGb} GB` : '-'} />
          <InfoRow label="OS" value={vps.osName ? `${vps.osName} ${vps.osVersion || ''}`.trim() : '-'} />
        </div>
      </div>

      {/* Purpose Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-sm font-medium text-gray-300">Purpose & Organization</h2>
        </div>
        <div className="card-body space-y-4">
          <InfoRow label="Purpose" value={vps.purpose || '-'} />
          <div>
            <p className="text-sm text-gray-500 mb-1">Projects</p>
            <div className="flex flex-wrap gap-2">
              {vps.projects.length > 0 ? (
                vps.projects.map((project) => (
                  <span key={project} className="px-2 py-1 bg-gray-800 rounded text-sm">
                    {project}
                  </span>
                ))
              ) : (
                <span className="text-gray-600">-</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Websites</p>
            <div className="flex flex-col gap-1">
              {vps.websites.length > 0 ? (
                vps.websites.map((url) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pulse-400 hover:text-pulse-300 text-sm flex items-center gap-1"
                  >
                    {url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ))
              ) : (
                <span className="text-gray-600">-</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Tags</p>
            <div className="flex flex-wrap gap-2">
              {vps.tags.length > 0 ? (
                vps.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 bg-pulse-500/10 text-pulse-400 rounded text-sm">
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-gray-600">-</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notes Card */}
      {vps.notes && (
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">Notes</h2>
          </div>
          <div className="card-body">
            <p className="text-sm text-gray-400 whitespace-pre-wrap">{vps.notes}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricsTab({ vpsId }: { vpsId: string }) {
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const config = TIME_RANGE_CONFIG[timeRange];

  const { data: systemData, isLoading: systemLoading } = useQuery({
    queryKey: ['vps', vpsId, 'metrics', 'system', timeRange],
    queryFn: () => {
      const from = new Date(Date.now() - config.ms);
      return api.get<SystemMetrics[]>(
        `/metrics/vps/${vpsId}/system?from=${from.toISOString()}&resolution=${config.resolution}`
      );
    },
    refetchInterval: 60000,
  });

  const { data: networkData, isLoading: networkLoading } = useQuery({
    queryKey: ['vps', vpsId, 'metrics', 'network', timeRange],
    queryFn: () => {
      const from = new Date(Date.now() - config.ms);
      return api.get<NetworkMetrics[]>(
        `/metrics/vps/${vpsId}/network?from=${from.toISOString()}&resolution=${config.resolution}`
      );
    },
    refetchInterval: 60000,
  });

  if (systemLoading || networkLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pulse-400" />
      </div>
    );
  }

  const system = systemData || [];
  const network = networkData || [];
  const latest = system[system.length - 1];

  // Compute network rates (bytes delta / time delta)
  const networkRates = network.length >= 2
    ? (() => {
        const last = network[network.length - 1]!;
        const prev = network[network.length - 2]!;
        const dt = (new Date(getTimestamp(last)).getTime() - new Date(getTimestamp(prev)).getTime()) / 1000;
        if (dt <= 0) return { rx: 0, tx: 0 };
        return {
          rx: Math.max(0, (last.rxBytes - prev.rxBytes) / dt),
          tx: Math.max(0, (last.txBytes - prev.txBytes) / dt),
        };
      })()
    : { rx: 0, tx: 0 };

  const formatTimeLabel = (m: any) => {
    const d = new Date(getTimestamp(m));
    if (timeRange === '7d') return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const cpuChartData = system.map((m) => ({
    time: formatTimeLabel(m),
    cpu: +m.cpuPercent.toFixed(1),
  }));

  const memChartData = system.map((m) => ({
    time: formatTimeLabel(m),
    ram: +m.ramPercent.toFixed(1),
    swap: m.swapTotalMb > 0 ? +((m.swapUsedMb / m.swapTotalMb) * 100).toFixed(1) : 0,
  }));

  const diskChartData = system.map((m) => ({
    time: formatTimeLabel(m),
    disk: +m.diskPercent.toFixed(1),
  }));

  // Network chart: compute per-interval rate
  const networkChartData = network.map((m, i) => {
    const time = formatTimeLabel(m);
    if (i === 0) return { time, rx: 0, tx: 0 };
    const prev = network[i - 1]!;
    const dt = (new Date(getTimestamp(m)).getTime() - new Date(getTimestamp(prev)).getTime()) / 1000;
    if (dt <= 0) return { time, rx: 0, tx: 0 };
    return {
      time,
      rx: Math.max(0, (m.rxBytes - prev.rxBytes) / dt),
      tx: Math.max(0, (m.txBytes - prev.txBytes) / dt),
    };
  });

  const cpuColor = latest
    ? latest.cpuPercent >= 90 ? 'text-red-400' : latest.cpuPercent >= 70 ? 'text-yellow-400' : 'text-green-400'
    : 'text-gray-400';

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-end gap-1">
        {(Object.keys(TIME_RANGE_CONFIG) as TimeRange[]).map((key) => (
          <button
            key={key}
            onClick={() => setTimeRange(key)}
            className={clsx(
              'px-3 py-1.5 text-sm rounded-md transition-colors',
              timeRange === key
                ? 'bg-pulse-500/20 text-pulse-400 font-medium'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            )}
          >
            {TIME_RANGE_CONFIG[key].label}
          </button>
        ))}
      </div>

      {/* Current Values Strip */}
      {latest && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <div className="card-body flex items-center gap-3">
              <div className="p-2 rounded-lg bg-sky-500/10">
                <Cpu className="h-5 w-5 text-sky-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">CPU</p>
                <p className={clsx('text-lg font-semibold', cpuColor)}>
                  {latest.cpuPercent.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <MemoryStick className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Memory</p>
                <p className="text-lg font-semibold text-gray-100">
                  {(latest.ramUsedMb / 1024).toFixed(1)}<span className="text-sm text-gray-500">/{(latest.ramTotalMb / 1024).toFixed(1)} GB</span>
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <HardDrive className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Disk</p>
                <p className="text-lg font-semibold text-gray-100">
                  {latest.diskUsedGb.toFixed(1)}<span className="text-sm text-gray-500">/{latest.diskTotalGb.toFixed(1)} GB</span>
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Network className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Network</p>
                <p className="text-sm font-medium text-gray-100">
                  <span className="text-green-400">{formatBytesRate(networkRates.rx)}</span>
                  {' / '}
                  <span className="text-blue-400">{formatBytesRate(networkRates.tx)}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {system.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12 text-gray-500">
            No metrics data available
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* CPU Chart */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-sm font-medium text-gray-300">CPU Usage</h2>
            </div>
            <div className="card-body">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cpuChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={11} tickLine={false} />
                    <YAxis stroke="#6b7280" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip {...chartTooltipStyle} formatter={(v: number) => [`${v}%`, 'CPU']} />
                    <Line type="monotone" dataKey="cpu" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Memory Chart */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-sm font-medium text-gray-300">Memory Usage</h2>
            </div>
            <div className="card-body">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={memChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={11} tickLine={false} />
                    <YAxis stroke="#6b7280" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip {...chartTooltipStyle} formatter={(v: number, name: string) => [`${v}%`, name === 'ram' ? 'RAM' : 'Swap']} />
                    <Legend formatter={(value) => value === 'ram' ? 'RAM' : 'Swap'} />
                    <Line type="monotone" dataKey="ram" stroke="#22c55e" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="swap" stroke="#f472b6" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Disk Chart */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-sm font-medium text-gray-300">Disk Usage</h2>
            </div>
            <div className="card-body">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={diskChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={11} tickLine={false} />
                    <YAxis stroke="#6b7280" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip {...chartTooltipStyle} formatter={(v: number) => [`${v}%`, 'Disk']} />
                    <Line type="monotone" dataKey="disk" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Network I/O Chart */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-sm font-medium text-gray-300">Network I/O</h2>
            </div>
            <div className="card-body">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={networkChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#6b7280" fontSize={11} tickLine={false} />
                    <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => formatBytes(v)} />
                    <Tooltip
                      {...chartTooltipStyle}
                      formatter={(v: number, name: string) => [formatBytesRate(v), name === 'rx' ? 'RX' : 'TX']}
                    />
                    <Legend formatter={(value) => value === 'rx' ? 'RX (in)' : 'TX (out)'} />
                    <Line type="monotone" dataKey="rx" stroke="#22c55e" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="tx" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const threatBadgeConfig: Record<ThreatLevel, { bg: string; text: string; label: string; border: string }> = {
  normal:     { bg: 'bg-green-500/10',  text: 'text-green-400',  label: 'Normal',     border: 'border-green-500/20' },
  elevated:   { bg: 'bg-blue-500/10',   text: 'text-blue-400',   label: 'Elevated',   border: 'border-blue-500/20' },
  concerning: { bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Concerning', border: 'border-yellow-500/20' },
  critical:   { bg: 'bg-red-500/10',    text: 'text-red-400',    label: 'Critical',   border: 'border-red-500/20' },
};

function SecurityTab({ vpsId }: { vpsId: string }) {
  const [tableFilter, setTableFilter] = useState<'all' | 'failed' | 'success'>('all');
  const queryClient = useQueryClient();

  const reviewMutation = useMutation({
    mutationFn: ({ ip, reviewed }: { ip: string; reviewed: boolean }) =>
      reviewed
        ? api.post(`/vps/${vpsId}/unreview-suspicious-ip`, { sourceIp: ip })
        : api.post(`/vps/${vpsId}/review-suspicious-ip`, { sourceIp: ip }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vps', vpsId, 'security-summary'] });
    },
  });

  // Pre-computed security summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['vps', vpsId, 'security-summary'],
    queryFn: () => api.get<VpsSecurityDetail>(`/vps/${vpsId}/security-summary?days=7`),
    refetchInterval: 60000,
  });

  // Raw attempts for table (filtered)
  const { data: attempts, isLoading: attemptsLoading } = useQuery({
    queryKey: ['vps', vpsId, 'ssh-attempts', tableFilter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (tableFilter === 'failed') params.set('success', 'false');
      if (tableFilter === 'success') params.set('success', 'true');
      return api.get<SshAttempt[]>(`/vps/${vpsId}/ssh-attempts?${params}`);
    },
    refetchInterval: 60000,
  });

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pulse-400" />
      </div>
    );
  }

  if (!summary || summary.stats.totalAttempts === 0) {
    return (
      <div className="card">
        <div className="card-body flex flex-col items-center justify-center py-16 text-gray-500">
          <Shield className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-lg mb-1">No SSH attempts recorded</p>
          <p className="text-sm text-gray-600">Attempts will appear here once the agent detects SSH login activity.</p>
        </div>
      </div>
    );
  }

  const badge = threatBadgeConfig[summary.threatLevel];

  return (
    <div className="space-y-6">
      {/* Threat Status Banner */}
      <div className={clsx('card border-l-4', badge.border)}>
        <div className="card-body flex items-start gap-4">
          <div className={clsx('p-3 rounded-lg', badge.bg)}>
            <Shield className={clsx('h-6 w-6', badge.text)} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-gray-100">Security Status</h2>
              <span className={clsx('status-badge', badge.bg, badge.text)}>
                {badge.label}
              </span>
            </div>
            {summary.threatReasons.map((reason, i) => (
              <p key={i} className="text-sm text-gray-400">{reason}</p>
            ))}
            {summary.suspiciousSuccessDetails && summary.suspiciousSuccessDetails.length > 0 && (() => {
              const allReviewed = summary.suspiciousSuccessDetails.every((e) => e.reviewed);
              return allReviewed ? (
                <div className="flex items-center gap-2 mt-3 rounded-lg bg-green-500/10 px-3 py-2">
                  <CheckCircle className="h-4 w-4 text-green-400 shrink-0" />
                  <span className="text-sm text-green-400">
                    {summary.suspiciousSuccessDetails.length} suspicious IP(s) reviewed and verified
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-3 rounded-lg bg-red-500/10 px-3 py-2">
                  <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
                  <span className="text-sm text-red-400">
                    {summary.suspiciousSuccessDetails.filter((e) => !e.reviewed).length} suspicious IP(s) need review — see details below
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Suspicious Success Details */}
      {summary.suspiciousSuccessDetails && summary.suspiciousSuccessDetails.length > 0 && (() => {
        const allReviewed = summary.suspiciousSuccessDetails.every((e) => e.reviewed);
        return (
        <div className={clsx('card border', allReviewed ? 'border-green-500/30' : 'border-red-500/30')}>
          <div className="card-header flex items-center gap-2">
            {allReviewed
              ? <CheckCircle className="h-4 w-4 text-green-400" />
              : <AlertTriangle className="h-4 w-4 text-red-400" />
            }
            <h2 className={clsx('text-sm font-medium', allReviewed ? 'text-green-400' : 'text-red-400')}>
              Suspicious Successful Logins
            </h2>
            <span className="text-xs text-gray-500 ml-auto">IPs that had both failed and successful login attempts</span>
          </div>
          <div className="card-body space-y-4">
            {summary.suspiciousSuccessDetails.map((entry) => (
              <div
                key={entry.ip}
                className={clsx(
                  'rounded-lg p-4 border',
                  entry.reviewed
                    ? 'bg-green-500/5 border-green-500/10'
                    : 'bg-red-500/5 border-red-500/10'
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {entry.country && (
                      <span className="text-sm">{countryCodeToFlag(entry.country)}</span>
                    )}
                    <span className="font-mono text-sm text-gray-100">{entry.ip}</span>
                    {entry.reviewed && (
                      <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Reviewed
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={clsx(
                      'text-xs px-2 py-1 rounded',
                      entry.reviewed ? 'text-gray-400 bg-gray-700/50' : 'text-red-400 bg-red-500/10'
                    )}>
                      {entry.failedAttempts.toLocaleString()} failed attempt{entry.failedAttempts !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={() => reviewMutation.mutate({ ip: entry.ip, reviewed: entry.reviewed })}
                      disabled={reviewMutation.isPending}
                      className={clsx(
                        'text-xs px-3 py-1 rounded transition-colors flex items-center gap-1',
                        entry.reviewed
                          ? 'text-gray-400 hover:text-red-400 hover:bg-red-500/10 bg-gray-700/50'
                          : 'text-green-400 hover:bg-green-500/20 bg-green-500/10'
                      )}
                    >
                      {entry.reviewed ? (
                        <>Undo</>
                      ) : (
                        <><CheckCircle className="h-3 w-3" /> Reviewed</>
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {entry.successfulLogins.map((login, i) => (
                    <div key={i} className="flex items-center gap-4 text-sm">
                      <span className="text-green-400 text-xs font-medium uppercase tracking-wide w-14 shrink-0">Login</span>
                      <span className="font-mono text-gray-300">{login.username}</span>
                      <span className="text-gray-500 ml-auto whitespace-nowrap" title={new Date(login.timestamp).toLocaleString()}>
                        {formatRelativeTime(login.timestamp)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        );
      })()}

      {/* Stats Cards */}
      <div className={clsx(
        'grid gap-4',
        summary.stats.suspiciousSuccesses > 0 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-3'
      )}>
        <div className="card">
          <div className="card-body flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Users className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Successful Logins (7d)</p>
              <p className="text-lg font-semibold text-green-400">
                {summary.stats.successfulAttempts.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Shield className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Blocked Probes (7d)</p>
              <p className="text-lg font-semibold text-gray-100">
                {summary.stats.failedAttempts.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Users className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Unique Attacker IPs</p>
              <p className="text-lg font-semibold text-gray-100">
                {summary.stats.uniqueAttackerIps.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        {summary.stats.suspiciousSuccesses > 0 && (
          <div className="card">
            <div className="card-body flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Suspicious Successes</p>
                <p className="text-lg font-semibold text-red-400">
                  {summary.stats.suspiciousSuccesses}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SSH Activity Timeline */}
      {summary.timeline.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-sm font-medium text-gray-300">SSH Activity (7 Days)</h2>
          </div>
          <div className="card-body">
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    tickFormatter={(d: string) => {
                      const date = new Date(d + 'T00:00:00');
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis stroke="#6b7280" fontSize={11} />
                  <Tooltip
                    {...chartTooltipStyle}
                    labelFormatter={(d: string) => {
                      const date = new Date(d + 'T00:00:00');
                      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    }}
                    formatter={(value: number, name: string) => [
                      value.toLocaleString(),
                      name === 'failed' ? 'Failed' : 'Successful',
                    ]}
                  />
                  <Legend formatter={(value) => (value === 'failed' ? 'Failed' : 'Successful')} />
                  <Line type="monotone" dataKey="failed" stroke="#6b7280" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="success" stroke="#22c55e" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Top Attackers + Top Usernames */}
      {summary.topAttackers.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Attackers */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-sm font-medium text-gray-300">Top Attackers</h2>
            </div>
            <div className="card-body space-y-3">
              {summary.topAttackers.map((attacker) => {
                const maxCount = summary.topAttackers[0]!.count;
                const pct = maxCount > 0 ? (attacker.count / maxCount) * 100 : 0;
                return (
                  <div key={attacker.ip}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          {attacker.country ? `${countryCodeToFlag(attacker.country)} ` : ''}
                        </span>
                        <span className="font-mono text-sm text-gray-300">{attacker.ip}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-600">{formatRelativeTime(attacker.lastSeen)}</span>
                        <span className="text-sm text-gray-400">
                          {attacker.count.toLocaleString()} <span className="text-xs text-gray-600">attempts</span>
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-500/60 rounded-full transition-all"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Targeted Usernames */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-sm font-medium text-gray-300">Targeted Usernames</h2>
            </div>
            <div className="card-body space-y-3">
              {summary.topUsernames.map((entry) => {
                const maxCount = summary.topUsernames[0]!.count;
                const pct = maxCount > 0 ? (entry.count / maxCount) * 100 : 0;
                return (
                  <div key={entry.username}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-sm text-gray-300">{entry.username}</span>
                      <span className="text-sm text-gray-400">
                        {entry.count.toLocaleString()} <span className="text-xs text-gray-600">attempts</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-500/60 rounded-full transition-all"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Recent SSH Attempts Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-300">Recent SSH Attempts</h2>
          <div className="flex gap-1">
            {(['all', 'failed', 'success'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setTableFilter(filter)}
                className={clsx(
                  'px-3 py-1 text-xs rounded-md transition-colors',
                  tableFilter === filter
                    ? 'bg-gray-700 text-gray-100'
                    : 'text-gray-500 hover:text-gray-300'
                )}
              >
                {filter === 'all' ? 'All' : filter === 'failed' ? 'Failed' : 'Successful'}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          {attemptsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pulse-400" />
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Source IP</th>
                  <th>Username</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Country</th>
                </tr>
              </thead>
              <tbody>
                {(attempts || []).map((attempt) => (
                  <tr key={attempt.id}>
                    <td className="text-gray-400 text-sm whitespace-nowrap" title={new Date(attempt.timestamp).toLocaleString()}>
                      {formatRelativeTime(attempt.timestamp)}
                    </td>
                    <td className="font-mono text-sm">{attempt.sourceIp}</td>
                    <td className="font-mono text-sm">{attempt.username}</td>
                    <td className="text-sm text-gray-400">{attempt.authMethod || '-'}</td>
                    <td>
                      <span
                        className={clsx(
                          'status-badge',
                          attempt.success ? 'status-online' : 'status-offline'
                        )}
                      >
                        {attempt.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      {attempt.geoCountry
                        ? `${countryCodeToFlag(attempt.geoCountry)} ${attempt.geoCountry}`
                        : '-'}
                    </td>
                  </tr>
                ))}
                {(attempts || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-8">
                      No {tableFilter === 'all' ? '' : tableFilter} attempts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const HEALTH_REFRESH_INTERVAL = 5000;

function ExternalTab({ vpsId }: { vpsId: string }) {
  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['vps', vpsId, 'health-checks'],
    queryFn: () => api.get<VpsHealthSummary>(`/vps/${vpsId}/health-checks`),
    refetchInterval: HEALTH_REFRESH_INTERVAL,
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const prevUpdatedAt = useRef(dataUpdatedAt);

  useEffect(() => {
    if (dataUpdatedAt !== prevUpdatedAt.current) {
      prevUpdatedAt.current = dataUpdatedAt;
      setRefreshKey((k) => k + 1);
    }
  }, [dataUpdatedAt]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pulse-400" />
      </div>
    );
  }

  const summary = data;

  if (!summary || summary.endpoints.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <Globe className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg mb-2">No endpoints configured</p>
        <p className="text-sm">Add websites to this VPS to start monitoring</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live indicator */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        <span>Live</span>
        <span className="text-gray-600">&bull;</span>
        <span>Updates every 5s</span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-sm text-gray-500">Endpoints</p>
          <p className="text-2xl font-semibold">{summary.endpoints.length}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Online</p>
          <p className="text-2xl font-semibold text-green-400">{summary.overallUp}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Down</p>
          <p className="text-2xl font-semibold text-status-offline">{summary.overallDown}</p>
        </div>
      </div>

      {/* Endpoint Details */}
      {summary.endpoints.map((ep) => (
        <EndpointCard key={`${ep.url}-${refreshKey}`} endpoint={ep} />
      ))}
    </div>
  );
}

function EndpointCard({ endpoint: ep }: { endpoint: EndpointHealth }) {
  // Reverse history so oldest is first (for chart left-to-right)
  const chartData = [...ep.history].reverse().map((h) => ({
    time: new Date(h.checkedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    ms: h.responseTimeMs,
    up: h.isUp ? 1 : 0,
  }));

  return (
    <div
      className="card progress-border progress-border-animate"
      style={{ '--duration': `${HEALTH_REFRESH_INTERVAL}ms` } as React.CSSProperties}
    >
      <div className="card-header">
        <div className="flex items-center gap-3">
          {ep.isUp ? (
            <CheckCircle className="h-5 w-5 text-green-400" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-red-400" />
          )}
          <a
            href={ep.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm text-pulse-400 hover:text-pulse-300"
          >
            {ep.url.replace(/^https?:\/\//, '')}
          </a>
          <span className={clsx(
            'status-badge ml-auto',
            ep.isUp ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
          )}>
            {ep.isUp ? 'Online' : 'Down'}
          </span>
        </div>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500">Status</p>
            <p className="text-lg font-semibold">{ep.statusCode || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Response Time</p>
            <p className={clsx(
              'text-lg font-semibold font-mono',
              ep.responseTimeMs < 300 ? 'text-green-400' :
              ep.responseTimeMs < 1000 ? 'text-yellow-400' : 'text-red-400'
            )}>
              {ep.responseTimeMs}ms
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Avg Response</p>
            <p className="text-lg font-semibold font-mono text-gray-300">{ep.avgResponseMs}ms</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Uptime</p>
            <p className={clsx(
              'text-lg font-semibold',
              ep.uptimePercent >= 99 ? 'text-green-400' :
              ep.uptimePercent >= 95 ? 'text-yellow-400' : 'text-red-400'
            )}>
              {ep.uptimePercent}%
            </p>
          </div>
        </div>

        {/* Response Time Chart */}
        {chartData.length > 1 && (
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="time"
                  stroke="#6B7280"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke="#6B7280"
                  tick={{ fontSize: 11 }}
                  unit="ms"
                  width={60}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB',
                  }}
                  formatter={(value: number) => [`${value}ms`, 'Response Time']}
                />
                <Line
                  type="monotone"
                  dataKey="ms"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className={clsx('text-gray-100', mono && 'font-mono text-sm')}>{value}</p>
    </div>
  );
}
